/**
 * LE RAPPORT DE SÉANCE : ce que l'écran de bilan envoie au serveur pour être
 * mis en page en PDF.
 *
 * Ce module ne CALCULE rien. Il sérialise ce que le bilan AFFICHE — les mêmes
 * objets, issus des mêmes appels à `acgSummary.js`, qui sont testés. Recalculer
 * ici, ou recalculer côté serveur en Python, donnerait deux versions du même
 * chiffre : celle qu'on projette et celle qu'on envoie aux participants. Le
 * jour où elles divergeraient, c'est le document envoyé qui mentirait, sans
 * que personne le voie. D'où la règle : CE QUI EST PROJETÉ EST CE QUI EST
 * ENVOYÉ, et le serveur ne fait que composer (cf. api/session_report.py).
 *
 * Les deux choses qu'il ajoute, lui, sont celles que l'écran ne peut pas
 * porter : le classement entre séances (il faut regarder d'autres parties) et
 * le TEXTE des mesures adoptées — l'écran n'en affiche que les titres, et les
 * descriptions vivent dans la collection `decisions`, que le front n'a jamais
 * chargée.
 *
 * LES GRAPHES PARTENT EN IMAGES. chart.js dessine dans un canvas ; aucune
 * bibliothèque serveur ne referait ces dessins à l'identique, et un rapport
 * dont les graphes ne ressemblent pas à ceux de la séance se lit comme un
 * autre document.
 */
import { CATEGORY_COLORS, NEUTRAL_COLOR, SECTOR_COLORS } from './acgSummary';

/**
 * Les graphes de la page, en PNG, relevés sur leurs canvas.
 *
 * Les cartes qui en portent un se déclarent par `data-report-chart="<clé>"` :
 * viser les canvas par leur ordre d'apparition ferait dépendre le rapport de
 * la mise en page, et un histogramme se retrouverait légendé « radar » le jour
 * où une carte serait déplacée.
 *
 * UN GRAPHE ABSENT N'EST PAS UNE ERREUR : la carte n'est pas montée (la partie
 * n'a pas de projection), ou le navigateur a vidé le canvas. Le serveur omet
 * alors la carte, le rapport sort quand même.
 */
export function captureCharts(root) {
  const charts = {};
  if (!root || typeof root.querySelectorAll !== 'function') return charts;
  root.querySelectorAll('[data-report-chart]').forEach((container) => {
    const key = container.getAttribute('data-report-chart');
    const canvas = container.querySelector('canvas');
    if (!key || !canvas || typeof canvas.toDataURL !== 'function') return;
    try {
      const image = canvas.toDataURL('image/png');
      // Un canvas jamais dessiné rend une data-URL minuscule : l'envoyer
      // ferait imprimer un rectangle vide en pleine page.
      if (image && image.length > 1024) charts[key] = image;
    } catch (error) {
      console.error(`Graphe « ${key} » non capturé :`, error);
    }
  });
  return charts;
}

/** Le bandeau de chiffres, dans l'ordre où l'écran les annonce. */
function figuresOf(gameStatus, view) {
  const figures = [{ value: String(view.measures.length), label: 'mesures adoptées' }];
  if (view.ledger.length > 0) {
    figures.push({ value: String(view.ledger.length), label: 'acteurs' });
  }
  if (gameStatus?.nb_rounds_cg > 0) {
    figures.push({ value: String(gameStatus.nb_rounds_cg), label: 'tours joués' });
  }
  return figures;
}

/**
 * Le corps de la requête. `view` est exactement ce que le composant a calculé
 * pour se dessiner — il est passé, pas recalculé.
 */
export function reportPayload({ pageData, gameStatus, view }) {
  const scores = gameStatus?.score_dic || {};
  return {
    title: pageData?.title || 'Le bilan de la séance',
    target_per_round: view.targetPerRound,
    view: {
      has_log: view.hasLog,
      objective: view.progress
        ? {
          percent: view.progress.percent,
          value: view.progress.value,
          target: view.progress.target,
          share: view.progress.share,
        }
        : null,
      figures: figuresOf(gameStatus, view),
      sectors: view.sectors.map((row) => ({
        label: row.label,
        value: row.value,
        share: row.share,
        // La couleur part avec la donnée : c'est `acgSummary` qui décide
        // qu'un secteur est orange, et l'histogramme juste au-dessus doit dire
        // la même chose que la barre.
        color: SECTOR_COLORS[row.key] || NEUTRAL_COLOR,
      })),
      categories: view.byCategory.map((row) => ({
        label: row.label,
        count: row.count,
        share: row.share,
        color: CATEGORY_COLORS[row.key] || NEUTRAL_COLOR,
      })),
      metrics: view.metrics
        .filter((id) => scores[id])
        .map((id) => ({ label: scores[id].name || id, value: scores[id].value })),
      debate: {
        divisive: view.divisive.map((row) => ({
          title: row.title, for: row.forCount, against: row.againstCount,
        })),
        consensual: view.consensual.map((row) => ({
          title: row.title, for: row.forCount, against: row.againstCount,
        })),
      },
      podium: view.top.map((actor) => ({
        rank: actor.rank, name: actor.name, value: actor.value,
      })),
      effort: view.hardest
        ? {
          name: view.hardest.name,
          conceded: view.hardest.conceded,
          value: view.hardest.value,
        }
        : null,
      actors: view.ledger
        .slice()
        .sort((a, b) => b.value - a.value)
        .map((actor) => ({
          name: actor.name,
          value: actor.value,
          delta: actor.initial != null ? actor.value - actor.initial : null,
          won: actor.won,
          conceded: actor.conceded,
        })),
    },
  };
}

/** Le nom de fichier choisi par le serveur, ou un repli. */
function filenameOf(response) {
  const header = response.headers?.get?.('Content-Disposition') || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : 'rapport_de_seance.pdf';
}

/**
 * Demande le rapport et rend `{ blob, filename }`.
 *
 * Le nom vient du serveur (`Content-Disposition`), qui connaît le nom de
 * l'atelier et la date de la séance — le front, lui, n'a que le titre de la
 * page. Il faut pour cela que l'en-tête soit exposée au JavaScript d'une autre
 * origine (`expose_headers`, app.py), sinon le rapport s'enregistrerait sous
 * un nom inventé.
 */
export async function requestReport(gameId, payload) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/session_report/${gameId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let detail = '';
    try {
      detail = (await response.json())?.error || '';
    } catch (error) {
      detail = '';
    }
    throw new Error(detail || `Le serveur a répondu ${response.status}.`);
  }
  return { blob: await response.blob(), filename: filenameOf(response) };
}
